import { DurationString, getDurationStringSeconds } from '@chronosphereio/core';
import { ParserTerms, parser } from '@chronosphereio/editors/prometheus';
import { produce } from 'immer';
import { Notice, NoticeKind, PrometheusQuerySpec, QueryMetadata, SupportedDataType } from '@/core';
import { getEmptyDataByDataType } from '@/plugin-system/utils';
import {
  getPrometheusAlignedTimeRange,
  getPrometheusClient,
  getPrometheusTimeRange,
  getRangeStep,
  InstantQueryResponse,
  MatrixData,
  PrometheusClient,
  queryHasIncreaseWithInterval,
  RangeQueryResponse,
  VectorData,
} from '../../model';
import { appMetadataToHTTPHeaders, injectDownsamplingStrategy } from '../../utils';
import { distributionResponseHandler } from './response-handlers/distribution-response-handler';
import { heatmapResponseHandler } from './response-handlers/heatmap-response-handler';
import { singleValueResponseHandler } from './response-handlers/single-value-response-handler';
import { singleValueTimeSeriesResponseHandler } from './response-handlers/single-value-timeseries-response-handler';
import { tableResponseHandler } from './response-handlers/table-response-handler';
import { timeSeriesResponseHandler } from './response-handlers/timeseries-response-handler';
import { BUCKET_LABEL } from './response-handlers/utils';
import { GetDataMethod, ResponseHandler, ResultContext, ResultType } from './types';
import { interpolateVariables } from './variables';

// For each supported data type, build out how we handle the result based on what type of result it is
const responseHandlers: Record<SupportedDataType, ResponseHandler> = {
  HeatMap: heatmapResponseHandler,
  Table: tableResponseHandler,
  SingleValue: singleValueResponseHandler,
  SingleValueTimeSeries: singleValueTimeSeriesResponseHandler,
  TimeSeries: timeSeriesResponseHandler,
  Distribution: distributionResponseHandler,
};

const defaultHandler = timeSeriesResponseHandler;

export const getData: GetDataMethod = async (rawSpec, context, additionalHeaders = {}, fetchFn) => {
  return getDataInternal(rawSpec, context, additionalHeaders, fetchFn);
};

const getDataInternal: GetDataMethod = async (rawSpec, context, additionalHeaders = {}, fetchFn) => {
  const alias = context.alias ?? '';

  const scrapeIntervalSeconds = context.config?.prometheusScrapeIntervalSeconds ?? 15;
  if (rawSpec.query === undefined || rawSpec.query === null || rawSpec.query === '') {
    // Do not make a request to the backend, instead return an empty Data
    return getEmptyDataByDataType(context.dataType ?? 'TimeSeries');
  }

  // Inject downsampling strategy into the query then interpolate variables
  let spec = produce(rawSpec, (draft: PrometheusQuerySpec) => {
    draft.query = injectDownsamplingStrategy(draft.query, draft.downsampling_strategy);
  });

  const isIncreaseWithInterval = queryHasIncreaseWithInterval(rawSpec.query);

  spec = interpolateVariables(spec, context, { isIncreaseWithInterval });

  const notices: Notice[] = [];

  const headers = context.appMetadata ? appMetadataToHTTPHeaders(context.appMetadata) : {};

  const minStep = getDurationStringSeconds(spec.min_step as DurationString);
  const timeRange = getPrometheusTimeRange(context.timeRange);
  const requestedEndTime = timeRange.end;

  const step = context.stepMs
    ? context.stepMs / 1000
    : getRangeStep({
        timeRange,
        minStepSeconds: minStep,
        suggestedStepMs: context.suggestedStepMs,
        scrapeIntervalSeconds,
        isIncreaseWithInterval,
      });
  const alignedTimerange = getPrometheusAlignedTimeRange(context.timeRange, step);

  let isStacked = false;
  let isStackedBar = false;
  if (context.panelDefinition?.spec.plugin.kind === 'TimeSeriesChart') {
    isStacked = context.panelDefinition?.spec.plugin.spec.visual?.stack === 'All';
    isStackedBar = isStacked && context.panelDefinition?.spec.plugin.spec.visual?.display === 'bar';
  }

  const start = alignedTimerange.start;
  const end = alignedTimerange.end;
  const query = spec.query;

  // Get the datasource, using the default Prom Datasource if one isn't specified in the query
  const client: PrometheusClient = getPrometheusClient(context.config, false, fetchFn);

  const requestStartMs = new Date().getTime();
  let truncationFn: string | undefined = spec.truncation_strategy;
  let truncationEnabled = context.queryLimits?.enabled ?? false;
  if (spec.truncation_strategy === 'Off') {
    truncationEnabled = false;
    truncationFn = undefined;
  }

  let truncationLimit = truncationEnabled ? context.queryLimits?.seriesLimit : undefined;
  let hasTruncationStrategySet = spec.truncation_strategy !== undefined;

  // Turn on truncation strategy by default for stacked bar charts to improve performance
  if (context.featureFlags.enableAutoStackedBarTruncation && isStackedBar) {
    hasTruncationStrategySet = true;
  }

  // If its stacked we have a different truncation limit and should use max
  if (isStacked && hasTruncationStrategySet && truncationEnabled) {
    truncationLimit = context.featureFlags.truncationStrategyLimit;
    if (truncationFn === undefined) {
      truncationFn = 'max';
    }
  }

  const downsamplingHeaders: Record<string, string> = {};
  if (spec.downsampling_strategy === 'LTTB') {
    // Use the scrape interval for the downsample step
    downsamplingHeaders['Chronosphere-Query-Step-Before-Result-Downsample'] = `${scrapeIntervalSeconds}s`;
  }

  const runInstantQuery = async (time: number = requestedEndTime) => {
    const response = await client.instantQuery(
      {
        query,
        time,
      },
      { ...headers, ...additionalHeaders }
    );
    return response;
  };

  const runRangeQuery = async (moreHeaders = additionalHeaders) => {
    const truncateBy = truncationFn?.toLowerCase() ?? 'avg';
    const response = await client.rangeQuery(
      {
        query,
        start,
        end,
        step,
        truncateAt: truncationLimit,
        truncateBy: truncateBy as 'min' | 'max' | 'avg',
      },
      { ...headers, ...moreHeaders, ...downsamplingHeaders }
    );
    return response;
  };

  let response: RangeQueryResponse;
  let compareResponse: RangeQueryResponse | undefined;

  const compareModeRequested =
    context.compareDurationInMs !== undefined &&
    context.compareDurationInMs !== null &&
    context.compareDurationInMs > 0;
  const compareModeEnabled = context.allowCompare && compareModeRequested;
  // Push a notice if the visualization does not support compare mode.
  if (compareModeRequested && !context.allowCompare) {
    notices.push({
      type: 'warning',
      message: 'Compare mode is not supported by this panel type.',
      kind: NoticeKind.CompareModeNotSupported,
    });
  }

  // Boolean that indicates if the results are from a range query
  let resultsAreFromRangeQuery: boolean;
  if (
    (spec.queryType === 'Instant' ||
      (context.dataType === 'SingleValue' && spec.queryType !== 'Range') ||
      (context.dataType === 'SingleValueTimeSeries' && spec.queryType !== 'Range') ||
      (spec.queryType !== 'Range' && context.dataType === 'Table')) &&
    isQueryInstantQueryCompatible(query)
  ) {
    resultsAreFromRangeQuery = false;
    const instantQueryResponse = await runInstantQuery();
    response = transformInstantQueryResponseToRangeQueryResponse(instantQueryResponse);

    // If compare mode is enabled, fetch again with the compare time
    if (compareModeEnabled && context.compareDurationInMs) {
      const durationInSeconds = context.compareDurationInMs / 1000;
      const compareTime = requestedEndTime - durationInSeconds;
      const compareInstantQueryResponse = await runInstantQuery(compareTime);
      compareResponse = transformInstantQueryResponseToRangeQueryResponse(compareInstantQueryResponse);
    }
  } else {
    resultsAreFromRangeQuery = true;
    response = await runRangeQuery();
  }

  let infoMessages: string[] = [];
  let warningMessages: string[] = [];
  if (response.status === 'success') {
    infoMessages = response.infos ?? [];
    warningMessages = response.warnings ?? [];
  }

  // Add all info warnings
  infoMessages.forEach((infoMessage) => {
    notices.push({
      type: 'info',
      appInteractionLevels: ['editable-edit', 'editable-view'],
      message: infoMessage,
    });
  });

  // Add all warning messages
  warningMessages.forEach((warningMessage) => {
    notices.push({
      type: 'warning',
      appInteractionLevels: ['editable-edit', 'editable-view'],
      message: warningMessage,
    });
  });

  if (response.status === 'success' && response.truncationDetails) {
    const totalSeries: number | undefined = response.truncationDetails.totalCount;
    const warningMessage = `Results truncated to ${truncationLimit} out of ${totalSeries} total series.`;

    const truncationNotice: Notice = {
      type: 'warning',
      kind: NoticeKind.TruncatedSeries,
      message: warningMessage,
      // Note: We chose not to say the exact limit because it varies by environment
      // and it is already included in the message
      description: 'A panel displaying more series can cause the browser to stall.',
    };
    if (context.queryLimits && context.queryLimits.setEnabled) {
      truncationNotice.action = {
        label: totalSeries ? `Load all ${totalSeries} series` : 'Load all series',
        isDanger: true,
        onClick: () => {
          if (context.queryLimits?.setEnabled) {
            context.queryLimits.setEnabled(false);
          }
        },
      };
    }
    notices.push(truncationNotice);
  }

  const requestDurationMs = new Date().getTime() - requestStartMs;

  const result = response.data?.result ?? [];
  const compareResult = compareResponse?.data?.result ?? [];

  const metadata: QueryMetadata = {
    notices,
    executedQueryString: query,
    stats: {
      responseTimeMs: requestDurationMs,
    },
  };

  let resultType: ResultType = ResultType.TimeSeries;
  if (result.length > 0) {
    const firstResult = result[0];
    if (firstResult && isNativeHistogram(firstResult)) {
      resultType = ResultType.NativeHistogram;
    } else if (isTimeSeriesHistogramResult(spec.query, result)) {
      resultType = ResultType.TimeSeriesHistogram;
    }
  }

  if (result.length === 0) {
    return getEmptyDataByDataType(context.dataType ?? 'TimeSeries', metadata);
  }

  const resultContext: ResultContext = {
    start,
    end,
    step,
    query,
    alias,
    series_name: spec.series_name,
    series_name_format: spec.series_name_format,
    spec,
    compareResult,
    compareDurationInMs: context.compareDurationInMs === null ? undefined : context.compareDurationInMs,
    runRangeQuery,
    resultsAreFromRangeQuery,
    response,
    hasTruncationStrategySet,
    isStacked,
  };

  const handler = context.dataType !== undefined ? responseHandlers[context.dataType] : defaultHandler;

  return handler[resultType](result, metadata, notices, resultContext);
};

export function isNativeHistogram(result: MatrixData['result'][0] | VectorData['result'][0]) {
  return 'histogram' in result || 'histograms' in result;
}

// Check if the query generated histograms by checking if it contains "_bucket" and if every series includes a "le" label.
export function isTimeSeriesHistogramResult(query: string, result: MatrixData['result']) {
  if (!/_bucket/.test(query)) return false;

  for (let i = 0; i < result.length; i++) {
    const series = result[i];
    if (series && !(BUCKET_LABEL in series.metric)) {
      return false;
    }
  }

  return true;
}

/**
 * Transforms an instant query response to a range query response.
 */
export function transformInstantQueryResponseToRangeQueryResponse(resp: InstantQueryResponse): RangeQueryResponse {
  if (resp.data?.resultType !== 'vector') {
    throw new Error('Instant query response is not a vector');
  }

  const result: MatrixData['result'] = [];
  resp.data?.result.forEach((r) => {
    if (r.value === undefined) {
      return;
    }
    result.push({
      metric: r.metric,
      values: [r.value],
    });
  });

  return {
    ...resp,
    data: {
      resultType: 'matrix',
      result,
    },
  };
}

/**
 * Checks if a query is compatible with instant queries.
 * Instant queries do not support modifiers (@start, @end) so since resolve to the same
 * timestamps
 */
export function isQueryInstantQueryCompatible(query: string): boolean {
  const ast = parser.parse(query);
  let hasModifiers = false;
  ast.iterate({
    enter(node) {
      if (node.type.is(ParserTerms.AtModifierPreprocessors)) {
        hasModifiers = true;
        return false;
      }
    },
  });
  return !hasModifiers;
}
